import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { eventsAPI } from '../../utils/api';
import { format } from 'date-fns';
import PublicLayout from '../../components/layout/PublicLayout';
import useSEO, { PAGE_SEO } from '../../hooks/useSEO';

const TABS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past',     label: 'Past Events' },
];

export default function EventsPage() {
  useSEO(PAGE_SEO.events);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [events, setEvents]       = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = activeTab === 'upcoming'
      ? { upcoming: true,  limit: 50 }
      : { past:     true,  limit: 50 };
    eventsAPI.getAll(params)
      .then(r => setEvents(r.data.data || []))
      .finally(() => setLoading(false));
  }, [activeTab]);

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 py-12">

        <div className="mb-8">
          <h1 className="section-title mb-2">Events</h1>
          <p className="text-gray-500">Gatherings, programmes, and Tijaniyyah activities</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all border ${
                activeTab === key
                  ? 'bg-navy-700 text-white border-navy-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-navy-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="h-64 bg-gray-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <div className="text-5xl mb-4">🌙</div>
            <p className="font-medium">
              {activeTab === 'upcoming'
                ? 'No upcoming events at this time.'
                : 'No past events to show.'}
            </p>
            <p className="text-sm mt-1">Check back soon, in sha Allah.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map(event => (
              <Link key={event.id} to={`/events/${event.slug}`} className="card group">
                <div className="h-44 relative overflow-hidden">
                  {event.cover_url
                    ? <img src={event.cover_url} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy-600 to-navy-900">
                        <span className="text-5xl">🌙</span>
                      </div>
                  }
                  <div className="absolute top-3 left-3 bg-white/95 text-navy-700 text-xs font-bold px-3 py-1.5 rounded-lg shadow">
                    {format(new Date(event.event_date), 'EEE, MMM d · h:mm a')}
                  </div>
                  {event.is_featured && (
                    <div className="absolute top-3 right-3 bg-cyan-400 text-navy-900 text-xs font-bold px-2 py-0.5 rounded-full">Featured</div>
                  )}
                  {activeTab === 'past' && (
                    <div className="absolute bottom-0 inset-x-0 bg-black/40 text-white text-xs font-medium text-center py-1.5">
                      Past Event
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-navy-800 mb-2 line-clamp-2 text-lg group-hover:text-cyan-600 transition-colors">{event.title}</h3>
                  {event.title_ar && <p className="arabic text-navy-600 text-base mb-2 line-clamp-1">{event.title_ar}</p>}
                  {event.location && <p className="text-gray-400 text-xs mb-3">📍 {event.location}</p>}
                  {event.description && (
                    <p className="text-gray-500 text-sm line-clamp-2" dangerouslySetInnerHTML={{ __html: event.description }} />
                  )}
                  <div className="mt-3 flex items-center text-cyan-600 text-xs font-semibold">
                    Read more →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
